import { useRef, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useInfiniteQuery } from '@tanstack/react-query'
import { handleGetRequest } from '../../utils/api-handler.util'
import { useAuth } from '../../contexts/AuthContext'
import LoadingSpinner from '../../components/common/LoadingSpinner'
import { PhoneIcon, ChevronLeftIcon } from '@heroicons/react/24/outline'
import {
  MESSAGE_REFETCH_INTERVAL_MILLIS,
  MESSAGING_CONSTANT,
} from '../../constants/messagingConstants'
import { scrollToBottom, maintainScrollPosition } from '../../utils/scrollUtils'
import NewMessageInput from '../../components/messaging/NewMessageInput'
import { QueryKeys } from '../../utils/queryKeyGenerator'
import GroupMessages from '../../components/messaging/GroupMessages'
import ReportButton from '../../components/common/ReportButton'
import { CONTENT_TYPES } from '../../constants/contentTypes'
import USER_TYPE from '../../constants/userType'
import DisplayShopName from '../../components/common/DisplayShopName'
import { CHAT_CONTEXT_TYPES } from '../../constants/contentTypes'

const ConversationPage = () => {
  const { conversationId } = useParams()
  const { currentUser } = useAuth()
  const navigate = useNavigate()

  const userType = currentUser.userType

  const scrollContainerRef = useRef(null)
  const topRef = useRef(null)
  const bottomRef = useRef(null)

  const containerRef = useRef(null)
  const initializedRef = useRef(false)

  const handleContainerRef = useCallback((node) => {
    if (node && !initializedRef.current) {
      containerRef.current = node
      scrollToBottom(containerRef, bottomRef, 'auto')
      initializedRef.current = true
    }
  }, [])

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useInfiniteQuery({
    queryKey: QueryKeys.listConversationMessages(conversationId, userType),
    queryFn: ({ pageParam = 1 }) =>
      handleGetRequest(`/${userType}s/messages/${conversationId}?page=${pageParam}`),
    getNextPageParam: (lastPage, allPages) => {
      return lastPage?.data?.length === MESSAGING_CONSTANT.PAGE_SIZE
        ? allPages.length + 1
        : undefined
    },
    enabled: !!conversationId,
    refetchInterval: MESSAGE_REFETCH_INTERVAL_MILLIS,
  })

  const conversation = data?.pages[0]?.conversation

  const messages = (data?.pages.flatMap((page) => page.data) ?? []).sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  )

  const handleScroll = useCallback(
    (e) => {
      const { scrollTop } = e.target
      const topReached = scrollTop === 0

      if (topReached && hasNextPage && !isFetchingNextPage) {
        const previousScrollHeight = scrollContainerRef.current.scrollHeight

        fetchNextPage().then(() => {
          maintainScrollPosition(scrollContainerRef, previousScrollHeight)
        })
      }
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage]
  )

  const getReportContentType = (otherUserType) => {
    if (otherUserType === USER_TYPE.retailer) {
      return CONTENT_TYPES.retailer
    }

    if (otherUserType === USER_TYPE.wholeseller) {
      return CONTENT_TYPES.wholeseller
    }

    return CONTENT_TYPES.customer
  }

  const renderReportButton = () => {
    return (
      <ReportButton
        contentId={conversation?.otherUser?.id}
        contentType={getReportContentType(conversation?.otherUser?.userType)}
        reportTarget={conversation?.otherUser?.name}
      />
    )
  }

  const getConversationTitle = () => {
    let otherUser = conversation?.otherUser

    if ([USER_TYPE.retailer, USER_TYPE.wholeseller].includes(otherUser?.userType)) {
      return (
        <DisplayShopName
          shop={{
            name: otherUser?.shopName,
            isPremium: otherUser?.isPremium,
            isVerified: otherUser?.isApproved,
          }}
        />
      )
    }

    return otherUser?.name
  }

  if (isLoading) return <LoadingSpinner />

  if (!conversation) return null

  const isProduct = conversation.contextType === CHAT_CONTEXT_TYPES.product
  const isBidding = conversation.contextType === CHAT_CONTEXT_TYPES.bidding
  const isFinder = conversation.contextType === CHAT_CONTEXT_TYPES.productFinder

  return (
    <div className="flex flex-col h-screen max-w-[1400px] mx-auto w-full">
      <>
        <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-background z-10">
          <button
            className="p-2 bg-secondary-600 text-white rounded-full"
            onClick={() => navigate('/messages')}
          >
            <ChevronLeftIcon className="h-5 w-5" />
          </button>
          <div className="flex-1 text-center font-semibold">{getConversationTitle()}</div>
          <div className=" flex items-center space-x-3  text-secondary-600 mx-1">
            <a
              href={`tel:${conversation?.otherUser?.phoneNumber}`}
              className="flex items-center space-x-1"
            >
              <PhoneIcon className="h-5 w-5" />
            </a>
            {renderReportButton()}
          </div>
        </div>
        {conversation?.contextData && (
          <div className="flex items-center p-2 border-b sticky top-0 bg-background z-10 rounded-lg shadow text-left">
            <img
              src={
                isProduct || isBidding
                  ? conversation.contextData.image
                  : conversation.contextData.productFinder?.imageUrl
              }
              alt={isProduct || isBidding ? conversation.contextData.name : 'Finder Request'}
              className="w-20 h-20 object-cover rounded-md"
            />
            <div className="ml-3 flex-1">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold text-gray-800">
                  {isProduct || isBidding ? conversation.contextData.name : 'Finder Request'}
                </h2>
                {(isBidding || isFinder) && (
                  <p className="text-sm text-secondary-600 font-semibold whitespace-nowrap ml-2">
                    {conversation.contextData.bidPriceText}
                  </p>
                )}
                {isProduct && (
                  <p className="text-sm text-secondary-600 font-semibold whitespace-nowrap ml-2">
                    {conversation.contextData.productPriceText}
                  </p>
                )}
              </div>
              <p className="text-sm text-gray-600">
                {isProduct || isBidding
                  ? conversation.contextData.description
                  : conversation.contextData.productFinder?.description}
              </p>
            </div>
          </div>
        )}
      </>
      <div
        className="flex-1 overflow-y-auto bg-neutral-200-100 px-4 py-2 space-y-4 pb-20"
        onScroll={handleScroll}
        ref={(node) => {
          scrollContainerRef.current = node
          handleContainerRef(node)
        }}
      >
        <div ref={topRef} />
        <GroupMessages messages={messages} />
        <div ref={bottomRef} className="h-4" />
      </div>

      <NewMessageInput
        handleScroll={() => {
          setTimeout(() => {
            scrollToBottom(scrollContainerRef, bottomRef, 'smooth')
          }, 200)
        }}
      />
    </div>
  )
}

export default ConversationPage
